import type { BIResponse } from '@/entities/bi'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type { TFunction } from 'i18next'
import type { CSSProperties } from 'react'
import type { WidgetPeriods } from '../types'

import { useMemo } from 'react'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { format, parseISO } from 'date-fns'
import { drop, every, head, map, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts'

import { DateAggregation, useBIAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { PALETTE } from '@/shared/lib/theme-palette'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { MainDashboardFilter } from '../../Filter'
import { WidgetContent, WidgetHeader } from '../../Widget'
import Widget from '../../Widget/Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorStateInside from '../../WidgetErrorStateInside'
import WidgetLoadingState from '../../WidgetLoadingState'

/**
 * Recharts (shadcn charts) port of {@link ./Widget} (Highcharts `column` +
 * `line`) for the charting-library migration spike (PCT-976): two column
 * series (Optimized / Accepted) plus an Accepted-percent line on a second
 * hidden 0-100 axis, rendered as a `ComposedChart`. The Highcharts
 * `scrollablePlotArea` maps to a `minWidth` on the chart container inside the
 * `overflow-x-auto` wrapper, and the tooltip keeps the Percent (line) /
 * Compact (column) number formats of the original. The single-category
 * `xAxis.max: 5` padding of the original has no Recharts equivalent and is
 * not reproduced.
 */

const BI_METRICS = ['optimized', 'accepted_total', 'accepted_total_percent']

type Props = {
	commonFilters: FilterSchema[]
	commonPeriods: WidgetPeriods
	className?: string
}

export default function PriceAcceptanceDynamicsWidget3({
	commonFilters = [],
	commonPeriods,
	className,
}: Props) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const filters = [...commonFilters, ...(params.filters ?? [])]
	const currency = useSelectedCurrency()
	const {
		data: biAnalytic,
		isLoading,
		isError,
	} = useBIAnalyticQuery(
		{
			currency,
			label: 'PriceAcceptanceDynamics',
			metrics: [...BI_METRICS],
			dimensions: [],
			filters,
			date_aggregation: DateAggregation.MonthKey,
			...commonPeriods?.main,
		},
		{
			enabled: !!commonPeriods?.main,
		}
	)
	const categories = useMemo(
		() => computeCategories(biAnalytic, t),
		[biAnalytic, t]
	)
	const chartData = useMemo(
		() =>
			zipSeries<Record<string, number>>(biAnalytic?.meta, biAnalytic?.data).map(
				(row, index) => ({
					...row,
					category: categories?.[index] ?? '',
				})
			),
		[biAnalytic, categories]
	)
	const chartConfig = {
		optimized: {
			label: t('widget.price_acceptance_dynamics.optimized.title', {
				defaultValue: 'Optimized',
			}),
			color: PALETTE.blue.DEFAULT,
		},
		accepted_total: {
			label: t('widget.price_acceptance_dynamics.accepted.title', {
				defaultValue: 'Accepted',
			}),
			color: PALETTE.green.DEFAULT,
		},
		accepted_total_percent: {
			label: t('widget.price_acceptance_dynamics_percent.accepted.title', {
				defaultValue: 'Accepted percent',
			}),
			color: 'var(--foreground)',
		},
	} satisfies ChartConfig
	const hasNoData = isEmptyDataset(biAnalytic)

	return (
		<Widget
			className={cn('relative flex min-h-62 flex-auto flex-col', className)}
		>
			<WidgetHeader
				title={t('widget.price_acceptance_dynamics.title', {
					defaultValue: 'Price Acceptance dynamics',
				})}
				tooltip={t('widget.price_acceptance_dynamics.tooltip', {
					defaultValue: 'widget.price_acceptance_dynamics.tooltip',
				})}
				subtitle={t('widget.price_acceptance_dynamics.subtitle', {
					defaultValue: 'Monthly CYTD',
				})}
			>
				<MainDashboardFilter
					irremovableCount={commonFilters.length}
					filters={filters ?? []}
					onChange={(filters) => {
						onChangeParams({ ...params, filters })
					}}
				/>
			</WidgetHeader>
			<WidgetLoadingState isTinted={isLoading} />

			<WidgetContent className='mt-auto'>
				{!hasNoData && !isLoading && !isError && (
					<div className='w-full overflow-x-auto'>
						<ChartContainer
							config={chartConfig}
							className='h-55 w-full'
							style={{ minWidth: chartData.length * 20 }}
						>
							<ComposedChart
								margin={{ top: 16, left: 8, right: 8 }}
								accessibilityLayer={false}
								data={chartData}
							>
								<CartesianGrid vertical={false} strokeDasharray='3 3' />
								<XAxis dataKey='category' axisLine={false} fontSize={10} />
								<YAxis yAxisId={0} hide />
								<YAxis yAxisId={1} hide orientation='right' domain={[0, 100]} />
								<ChartTooltip
									content={
										<ChartTooltipContent
											formatter={(value, name, item) => (
												<div className='flex w-full items-center gap-2'>
													<div
														className='h-2.5 w-2.5 shrink-0 rounded-[2px] bg-(--color-bg)'
														style={
															{
																'--color-bg': item.color ?? item.fill,
															} as CSSProperties
														}
													/>
													<span className='text-background/50'>
														{chartConfig[name as keyof typeof chartConfig]
															?.label ?? name}
													</span>
													<span className='ml-auto font-mono font-semibold text-background tabular-nums'>
														{formatNumber(
															value as number,
															name === 'accepted_total_percent'
																? NumberFormats.Percent
																: NumberFormats.Compact
														)}
													</span>
												</div>
											)}
										/>
									}
								/>
								<Bar
									dataKey='optimized'
									yAxisId={0}
									fill='var(--color-optimized)'
								/>
								<Bar
									dataKey='accepted_total'
									yAxisId={0}
									fill='var(--color-accepted_total)'
								/>
								<Line
									dataKey='accepted_total_percent'
									yAxisId={1}
									type='linear'
									stroke='var(--color-accepted_total_percent)'
									strokeWidth={2}
									//dot={false}
								/>
							</ComposedChart>
						</ChartContainer>
					</div>
				)}
				{hasNoData && !isLoading && isError && <WidgetErrorStateInside />}
				{hasNoData && !isLoading && !isError && <WidgetEmptyState />}
			</WidgetContent>
		</Widget>
	)
}
const isEmptyDataset = pipe(
	prop('data'),
	every(
		pipe(
			drop(1),
			every((v) => v === 0)
		)
	)
)
const computeCategories = (
	biAnalytic: BIResponse | null | undefined,
	t: TFunction
) =>
	pipe(
		prop('data'),
		map(head)
	)(biAnalytic)?.map((date: string, i: number, data: string[]) => {
		const month = format(parseISO(date), 'MMM')
		const isLast = i === data.length - 1

		return isLast ? `${month} (${t('mtd', { defaultValue: 'MTD' })})` : month
	})
